import { cn } from "@util/tailwind";

interface AIPersonaOverlayProps {
    name: string;
    imageSrc?: string;
    isActive?: boolean;
    isSpeaking?: boolean;
    className?: string;
    logoSrc?: string; // Custom logo path
    primaryColor?: 'blue' | 'black'; // Color scheme
}

export default function AIPersonaOverlay({
    name,
    imageSrc,
    isActive = true,
    isSpeaking = false,
    className,
    logoSrc,
    primaryColor = 'blue',
}: AIPersonaOverlayProps) {
    // Color classes based on primaryColor
    const colorClasses = primaryColor === 'black'
        ? {
            bg: isSpeaking ? 'bg-gray-800' : 'bg-black',
            gradient: 'from-gray-400 to-gray-500',
            border: 'border-gray-400',
            borderLight: 'border-gray-300',
            borderLighter: 'border-gray-200',
        }
        : {
            bg: isSpeaking ? 'bg-blue-700' : 'bg-blue-600',
            gradient: 'from-blue-400 to-blue-500',
            border: 'border-blue-400',
            borderLight: 'border-blue-300',
            borderLighter: 'border-blue-200',
        };
    return (
        <div
            className={cn(
                "relative rounded-lg overflow-hidden border border-gray-700 shadow-lg",
                className
            )}
        >
            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                {/* Circle with logo */}                <div
                    className={cn(
                        "relative w-16 h-16 rounded-full flex items-center justify-center",
                        colorClasses.bg
                    )}
                >{/* Logo (using "O" as a simple logo for ourteam) */}
                    {/* <span className="text-white text-2xl font-bold">O</span> */}
                    <div>
                        <img
                            src={logoSrc || `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                            alt={`${name} logo`}
                            className="w-full h-full object-contain"
                        />
                    </div>                    {/* Pulsing ring effect when speaking */}
                    {isActive && isSpeaking && (
                        <div className="absolute inset-0 w-full h-full">
                            {/* Inner glowing circle */}
                            <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${colorClasses.gradient} opacity-20 animate-pulse`}></div>

                            {/* Primary animated ring - thicker border */}
                            <div className={`absolute inset-[2px] rounded-full border-4 ${colorClasses.border} animate-pulse-slow`}></div>

                            {/* Outer ripple effects - thicker borders */}
                            <div className={`absolute inset-0 rounded-full border-2 ${colorClasses.border} animate-ripple delay-0`}></div>
                            <div className={`absolute inset-0 rounded-full border-2 ${colorClasses.borderLight} animate-ripple delay-300`}></div>
                            <div className={`absolute inset-0 rounded-full border-2 ${colorClasses.borderLighter} animate-ripple delay-600`}></div>
                        </div>
                    )}
                </div>
            </div>

            {/* Active indicator moved to top right - REMOVED */}
            {isActive && !isSpeaking && (
                <div className="absolute top-2 right-2 flex items-center gap-1.5">
                    {/* Badge removed as requested */}
                    <span className="sr-only">Active</span>
                </div>
            )}

            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center justify-center">
                    <span className="text-sm text-white font-medium truncate">
                        {name}
                    </span>
                </div>
            </div>
        </div>
    );
}